import React, { useCallback, useEffect, useMemo } from "react";
import { ironsClub, driversClub } from "../../../constants/clubs";

import { MdHelpOutline } from "react-icons/md";
import {
  buildMetric,
  formatPrimaryValue,
  formatTourDeviation,
  formatZScore,
  getDeviationColorClass,
  normalizeZScore,
  normalizeZScoreColor,
  summarizeIndicatorsByClub,
} from "../../../utils/metrics";
import { useSwingLibraryStore } from "../../../store/swingLibraryStore";
import { Tooltip } from "../../../../../components/shared/Tooltip/Tooltip";
import { useSwingLibrarySettings } from "../../../hooks/useSwingLibrarySettings";
import { useSwingList } from "../../../hooks/useSwingList";
import { LOCKED_COLUMN_COUNT } from "../constants/lockedColumnCount";
import Select from "../../../../../components/shared/Select/Select";
import DriverIcon from "../../../../../components/shared/icons/DriverIcon";
import IronIcon from "../../../../../components/shared/icons/IronIcon";
import { twMerge } from "tailwind-merge";

// Converts the server-side `averages` payload into the
// { Driver: { indicatorId: metric }, Iron: { indicatorId: metric } } shape that
// buildMetric/renderAvgCells expect.
// Real API shape per indicator: { average, deviation, zScore (plain number) }
function buildSummaryFromAverages(averages = {}) {
  const toMetricMap = (indicators = {}) =>
    Object.entries(indicators).reduce((acc, [id, data]) => {
      acc[id] = {
        avg: data?.average ?? data?.avg ?? data?.indicatorValue ?? null,
        zScore: normalizeZScore(data?.zScore),
        zScoreColor: normalizeZScoreColor(data?.zScore),
        unit: data?.unit ?? null,
        stdDev: data?.deviation ?? data?.stdDev ?? null,
        tourAvg: data?.deviation ?? null,
        count: data?.count ?? null,
      };
      return acc;
    }, {});

  const driverRaw = averages?.D ?? averages?.driver ?? averages?.Driver ?? {};
  const ironRaw = averages?.MI ?? averages?.iron ?? averages?.Iron ?? {};

  return {
    Driver: toMetricMap(driverRaw),
    Iron: toMetricMap(ironRaw),
  };
}

// type AvgRowsProps = {
//   allVideos: VideoT[]
//   averages?: object        // pre-computed averages from API response
//   columnOrder: string[]
//   columns: MetricColumn[]
//   selectedTableData?: VideoT[]
// }

export function AvgRows({
  allVideos,
  averages: averagesProp,
  columnOrder,
  columns,
  selectedTableData,
}) {
  const { data: swingListData } = useSwingList();

  const { data: swingLibrarySettings } = useSwingLibrarySettings();

  const { activeClub, activeFilters, setActiveClub } = useSwingLibraryStore();

  // 1) Pre-compute summarized aggregates (Driver/Iron)
  //    Priority: prop averages > hook averages > local computation
  //    When rows are selected, fall back to local computation for that subset.
  const summary = useMemo(() => {
    const hasSelection = selectedTableData && selectedTableData.length > 0;
    const resolvedAverages = averagesProp ?? swingListData?.averages;

    if (!hasSelection && resolvedAverages) {
      return buildSummaryFromAverages(resolvedAverages);
    }

    return summarizeIndicatorsByClub(allVideos, selectedTableData, {});
  }, [allVideos, averagesProp, selectedTableData, swingListData?.averages]);

  // 2) Ordered metric columns (skip first 4 locked columns)
  const orderedMetricColumns = useMemo(() => {
    const merged = [];

    columnOrder.forEach((id) => {
      const col = columns.find((c) => c.id === id);
      if (!col) return;

      const indicator = swingLibrarySettings?.savedIndicators?.find(
        (f) => f.id === id,
      );

      merged.push({
        ...col,
        id,
        unit: indicator?.unit ?? col.unit,
      });
    });

    return merged.slice(LOCKED_COLUMN_COUNT);
  }, [columnOrder, columns, swingLibrarySettings?.savedIndicators]);

  // 3) Render a single cell set for a club type ("MI" -> Iron, "D" -> Driver)
  const renderAvgCells = useCallback(
    (type) => {
      const club = type === "MI" ? "Iron" : "Driver";
      return orderedMetricColumns.map((col) => {
        const metric = buildMetric(summary[club], col.id);

        const unit = col.unit;

        return (
          <td className="relative px-2 text-start" key={`${type}-${col.id}`}>
            <div className="flex flex-col items-start justify-center">
              <div
                className={`text-sm font-semibold ${
                  swingLibrarySettings?.tourAveragesToggle &&
                  getDeviationColorClass(metric)
                }`}
              >
                {formatPrimaryValue(metric, unit)}
              </div>
              {swingLibrarySettings?.tourAveragesToggle && (
                <div className="text-gray600 text-sm">{metric.tourAvg}</div>
              )}
              <div className="text-sm font-semibold text-black">
                {formatZScore(metric)}
              </div>
            </div>
            <div className="bg-gray200 absolute top-0 right-0 h-full w-px" />
          </td>
        );
      });
    },
    [orderedMetricColumns, summary, swingLibrarySettings?.tourAveragesToggle],
  );

  const clubsInData = useMemo(() => {
    const clubs = allVideos.map((r) => r?.userData?.golfClub).filter(Boolean);
    return new Set(clubs);
  }, [allVideos]);

  const showClub = useCallback(
    (clubArray) => {
      const existsInData = clubArray.some((c) => clubsInData.has(c));
      if (!existsInData) return false;

      if (!activeFilters.club?.length) return true;

      return activeFilters.club.some((club) => clubArray.includes(club));
    },
    [clubsInData, activeFilters.club],
  );

  const selectOptions = useMemo(() => {
    const driverAvailable = showClub(driversClub);
    const ironsAvailable = showClub(ironsClub);

    return [
      {
        value: "D",
        text: "Driver Averages",
        subtext: "Show averages for Driver clubs",
        icon: <DriverIcon className="text-gray900 h-5 w-5 min-w-5" />,
        disabled: !driverAvailable,
        tooltip: driverAvailable ? undefined : "No Driver swings in this view",
      },
      {
        value: "MI",
        text: "Irons Averages",
        subtext: "Show averages for Irons clubs",
        icon: <IronIcon className="text-gray900 h-5 w-5 min-w-5" />,
        disabled: !ironsAvailable,
        tooltip: ironsAvailable ? undefined : "No Iron swings in this view",
      },
    ];
  }, [showClub]);

  useEffect(() => {
    const driverAvailable = showClub(driversClub);
    const ironsAvailable = showClub(ironsClub);

    if (!driverAvailable && ironsAvailable) {
      setActiveClub("MI");
      return;
    }

    if (!ironsAvailable && driverAvailable) {
      setActiveClub("D");
    }
  }, [setActiveClub, showClub]);

  const activeLabel = activeClub === "D" ? "Driver Averages" : "Irons Averages";

  return (
    <tr
      className="bg-gray100 border-collapse"
      style={{
        height: "75px",
      }}
    >
      <td className="min-w-0 px-2 text-start" colSpan={LOCKED_COLUMN_COUNT / 2}>
        <Select
          btnInitialColor="gray100"
          btnSelectedColor="gray100"
          buttonProps={{
            className:
              "w-full justify-between rounded-2xl px-4 py-3 text-base font-semibold min-w-0",
          }}
          className="w-full"
          onChange={(val) => setActiveClub(val)}
          options={selectOptions}
          placeholder={activeLabel}
          showSelectedText
          value={activeClub}
          menuClassName="w-64"
          renderOption={(opt, { isChecked }) => (
            <>
              {opt.icon}

              <div className="min-w-0 flex-1 flex flex-col items-start justify-between gap-0.5">
                <div className="truncate text-sm font-semibold text-gray900">
                  {opt.text}
                </div>
                {opt.subtext ? (
                  <div className="truncate text-xs text-gray600">
                    {opt.subtext}
                  </div>
                ) : null}
              </div>
            </>
          )}
          renderSelected={(opt, open) => (
            <div className="flex min-w-0 flex-row items-center gap-2 overflow-hidden">
              {open ? null : opt.icon}
              <div className="truncate min-w-0 flex-1">{opt.text}</div>
            </div>
          )}
        />
      </td>
      <td
        className="relative px-2 text-start"
        colSpan={LOCKED_COLUMN_COUNT / 2}
      >
        <div className="flex flex-col">
          <div className="font-manrope text-gray900 text-sm font-bold">
            Average ({activeClub === "D" ? "Driver" : "Irons"})
          </div>

          {swingLibrarySettings?.tourAveragesToggle && (
            <div className="font-manrope text-gray600 text-sm font-medium">
              Deviation from Tour Avg
            </div>
          )}
          <div className="flex items-center gap-1">
            <div className="font-manrope text-gray600 text-sm font-medium">
              Z Score
            </div>

            <Tooltip
              content={
                <div className="text-sm text-white">
                  The Z-Score measures how many standard deviations this swing
                  is away from your average
                </div>
              }
            >
              <MdHelpOutline className="text-gray500 size-4 cursor-pointer" />
            </Tooltip>
          </div>
        </div>
        <div className="bg-gray200 absolute top-0 right-0 h-full w-px" />
      </td>
      {renderAvgCells(activeClub)}
    </tr>
  );
}

export default AvgRows;
